// 该组件只会用在移动端上！
'use client';

import Button from '@/components/Button';
import logger from '@/utils/client-logger';
import { colors, font } from '@/theme/zeelool.theme';
import { Box, Drawer, DrawerProps, IconButton, SxProps, Theme, Typography } from '@mui/material';
import { NewCloseOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import {
  ReactNode,
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

export type PopupPosition = 'top' | 'bottom' | 'left' | 'right';

export interface PopupProps {
  /**
   * 是否显示弹出层
   */
  open?: boolean;
  /**
   * 弹出位置
   * @default 'bottom'
   */
  position?: PopupPosition;
  /**
   * 自定义宽度，仅 position 为 left 或 right 时有效
   * @default '60%'
   */
  width?: string | number;
  /**
   * 自定义高度，仅 position 为 top 或 bottom 时有效
   * @default 'auto'
   */
  height?: string | number;
  /**
   * 是否显示圆角
   * @default true
   */
  round?: boolean;
  /**
   * 圆角大小(px)
   * @default 16
   */
  borderRadius?: number;
  /**
   * 是否显示遮罩层
   * @default true
   */
  overlay?: boolean;
  /**
   * 点击遮罩层是否关闭弹出层
   * @default true
   */
  closeOnClickOverlay?: boolean;
  /**
   * 弹出层标题
   */
  title?: ReactNode;
  /**
   * 无可见标题或视觉标题不可作为名称时，提供弹出层的可访问名称
   */
  ariaLabel?: string;
  /**
   * 是否隐藏关闭图标
   * @default false
   */
  hideCloseIcon?: boolean;
  /**
   * 弹出层内容
   */
  children?: ReactNode;
  /**
   * 弹出层内容最大高度
   * @default 'auto'
   */
  childrenMaxHeight?: string;
  /**
   * 自定义样式
   */
  style?: React.CSSProperties;
  /**
   * 自定义类名
   */
  className?: string;
  /**
   * 自定义body类名
   */
  bodyClassName?: string;
  /**
   * 关闭弹出层时触发
   */
  onClose?: () => void;
  /**
   * 打开弹出层时触发
   */
  onOpen?: () => void;
  /**
   * 弹出层进入动画完成时触发
   */
  onEntered?: () => void;
  /**
   * 弹出层退出动画完成时触发
   */
  onExited?: () => void;
  /**
   * 禁用自动获取焦点，解决焦点管理问题
   * @default false
   */
  disableAutoFocus?: boolean;
  /**
   * 挂载时是否保留组件，避免内容丢失
   * @default false
   */
  keepMounted?: boolean;
  /**
   * 关闭状态下是否将正文写入服务端 HTML
   * @default false
   */
  renderContentOnServer?: boolean;
  /**
   * 自定义样式
   */
  sx?: SxProps<Theme>;
  /**
   * 是否显示标题下方的分割线
   * @default true
   */
  showDivider?: boolean;
  /**
   * 容器元素，用于控制挂载位置
   * 如果为 null，则挂载到 body
   * 如果为 HTMLElement，则挂载到指定元素
   */
  container?: HTMLElement | null;
  /**
   * 自定义 z-index 值
   */
  zIndex?: number;
  /**
   * 底部操作区内容
   */
  footer?: ReactNode;
  /**
   * 是否显示底部区域
   * @default false
   */
  hasFooter?: boolean;
  /**
   * 确认按钮文本
   * @default 'Ok'
   */
  okText?: string;
  /**
   * 确认按钮是否禁用
   * @default false
   */
  okDisabled?: boolean;
  /**
   * 取消按钮文本
   * @default 'Cancel'
   */
  cancelText?: string;
  /**
   * 确认按钮点击回调
   */
  onOk?: () => void;
  /**
   * 取消按钮点击回调
   */
  onCancel?: () => void;
  /**
   * 是否显示加载状态
   * @default false
   */
  loading?: boolean;
  /**
   * 禁用滚动锁定功能。
   * @default false
   */
  disableScrollLock?: boolean;
  /**
   * 主内容区 DOM id，供外部 aria-controls 等引用
   */
  contentContainerId?: string;
}

export interface PopupRef {
  open: () => void;
  close: () => void;
}

const getAnchor = (position: PopupPosition): DrawerProps['anchor'] => {
  switch (position) {
    case 'top':
      return 'top';
    case 'bottom':
      return 'bottom';
    case 'left':
      return 'left';
    case 'right':
      return 'right';
    default:
      return 'bottom';
  }
};

const Popup = forwardRef<PopupRef, PopupProps>(
  (
    {
      open = false,
      position = 'bottom',
      width = '60%',
      height = 'auto',
      round = true,
      borderRadius = 16,
      overlay = true,
      closeOnClickOverlay = true,
      title,
      ariaLabel,
      children,
      style,
      className = '',
      bodyClassName = '',
      childrenMaxHeight = 'auto',
      onClose,
      onOpen,
      onEntered,
      onExited,
      disableScrollLock = false,
      disableAutoFocus = false,
      keepMounted = false,
      renderContentOnServer = false,
      sx,
      showDivider = true,
      hideCloseIcon = false,
      container = null,
      zIndex,
      footer,
      hasFooter = false,
      okText,
      okDisabled = false,
      cancelText,
      onOk,
      onCancel,
      loading = false,
      contentContainerId,
    },
    ref,
  ) => {
    const [visible, setVisible] = useState(open);
    const [canUsePortal, setCanUsePortal] = useState(!renderContentOnServer);
    const titleId = useId();
    const titleTextRef = useRef<HTMLDivElement | null>(null);
    const closeButtonRef = useRef<HTMLButtonElement | null>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);
    const wasOpenRef = useRef(false);
    const rafIdRef = useRef<number | null>(null);
    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
    const t = useTranslations('dialog');

    // 同步外部open状态
    useEffect(() => {
      setVisible(open);
    }, [open]);

    useEffect(() => {
      if (renderContentOnServer) {
        setCanUsePortal(true);
      }
    }, [renderContentOnServer]);

    useImperativeHandle(ref, () => ({
      open: () => {
        setVisible(true);
        onOpen?.();
      },
      close: () => {
        setVisible(false);
        onClose?.();
      },
    }));

    const handleClose = () => {
      if (closeOnClickOverlay) {
        setVisible(false);
        onClose?.();
      }
    };

    const defaultOkText = okText || t('okText');
    const defaultCancelText = cancelText || t('cancelText');

    const anchor = getAnchor(position);
    const isHorizontal = position === 'left' || position === 'right';
    const isVertical = position === 'top' || position === 'bottom';

    // 构建圆角样式
    const borderRadiusStyle = (() => {
      if (!round) return {};

      switch (position) {
        case 'top':
          return {
            borderBottomLeftRadius: `${borderRadius}px`,
            borderBottomRightRadius: `${borderRadius}px`,
          };
        case 'bottom':
          return {
            borderTopLeftRadius: `${borderRadius}px`,
            borderTopRightRadius: `${borderRadius}px`,
          };
        case 'left':
          return {
            borderTopRightRadius: `${borderRadius}px`,
            borderBottomRightRadius: `${borderRadius}px`,
          };
        case 'right':
          return {
            borderTopLeftRadius: `${borderRadius}px`,
            borderBottomLeftRadius: `${borderRadius}px`,
          };
        default:
          return {};
      }
    })();

    const hasStringTitle = typeof title === 'string' && title.length > 0;
    const paperAria = ariaLabel
      ? { 'aria-label': ariaLabel }
      : hasStringTitle
        ? { 'aria-labelledby': titleId }
        : { 'aria-label': t('defaultDialogAriaLabel') };

    // 清理所有的定时器和 requestAnimationFrame
    const cleanupFocusTimers = () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (timeoutIdRef.current !== null) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };

    useLayoutEffect(() => {
      // 清理任何未完成的焦点操作
      if (!visible) {
        cleanupFocusTimers();
      }

      if (visible) {
        if (!wasOpenRef.current) {
          previousFocusRef.current = document.activeElement as HTMLElement | null;
          wasOpenRef.current = true;

          if (!disableAutoFocus) {
            const setFocus = () => {
              if (hasStringTitle && titleTextRef.current) {
                titleTextRef.current.focus();
              } else if (!hideCloseIcon && closeButtonRef.current) {
                closeButtonRef.current.focus();
              }
            };

            // 首先尝试使用 RAF
            rafIdRef.current = requestAnimationFrame(() => {
              cleanupFocusTimers();
              try {
                setFocus();
              } catch (err) {
                logger.error('[Popup] Failed to focus via RAF:', err);
              }
            });

            // 添加 timeout 作为后备，确保焦点最终被设置
            timeoutIdRef.current = setTimeout(() => {
              try {
                setFocus();
              } catch (err) {
                logger.error('[Popup] Failed to focus via timeout:', err);
              }
            }, 100);

            return cleanupFocusTimers;
          }
        }
      } else if (wasOpenRef.current) {
        wasOpenRef.current = false;
        cleanupFocusTimers();

        // 恢复之前的焦点
        const restoreFocus = () => {
          if (previousFocusRef.current) {
            try {
              previousFocusRef.current.focus();
            } catch (err) {
              logger.error('[Popup] Failed to restore focus:', err, previousFocusRef.current);
            } finally {
              previousFocusRef.current = null;
            }
          }
        };

        rafIdRef.current = requestAnimationFrame(restoreFocus);
        timeoutIdRef.current = setTimeout(restoreFocus, 50);

        return cleanupFocusTimers;
      }
    }, [visible, disableAutoFocus, title, hideCloseIcon, hasStringTitle]);

    // 组件卸载时清理所有定时器
    useEffect(() => {
      return cleanupFocusTimers;
    }, []);

    return (
      <Drawer
        open={visible}
        anchor={anchor}
        onClose={handleClose}
        hideBackdrop={!overlay}
        disableScrollLock={disableScrollLock}
        disableAutoFocus
        keepMounted={keepMounted || renderContentOnServer}
        disablePortal={renderContentOnServer && !canUsePortal}
        disableEnforceFocus={false}
        disableRestoreFocus
        container={container}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-modal': true,
            ...paperAria,
          },
          transition: {
            onEntered,
            onExited,
          },
        }}
        sx={{
          zIndex,
          '& .MuiDrawer-paper': {
            width: isHorizontal ? width : '100%',
            height: isVertical ? height : '100%',
            backgroundColor: colors.primaryWhite,
            ...borderRadiusStyle,
            fontFamily: font.br,
            ...style,
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
          ...sx,
        }}
        className={className}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {typeof title === 'string' && title.length > 0 ? (
            <Box
              className="popup-title"
              sx={{
                padding: '16px 50px',
                borderBottom: showDivider ? '1px solid #f4f4f4' : 'none',
                textAlign: 'center',
                position: 'relative',
              }}
            >
              <Typography
                ref={titleTextRef}
                id={titleId}
                tabIndex={-1}
                component="div"
                className="popup-title-text"
                sx={{
                  fontSize: '16px',
                  color: colors.primaryBlack,
                  fontFamily: font.be,
                  lineHeight: '20px',
                  outline: 'none',
                }}
              >
                {title}
              </Typography>
              {!hideCloseIcon && (
                <IconButton
                  ref={closeButtonRef}
                  aria-label={t('closeAriaLabel')}
                  onClick={() => {
                    setVisible(false);
                    onClose?.();
                  }}
                  sx={{
                    position: 'absolute',
                    right: 6,
                    top: 10,
                    padding: '4px',
                  }}
                  size="small"
                >
                  <NewCloseOutlined className="text-[24px] text-primary-text" aria-hidden />
                </IconButton>
              )}
            </Box>
          ) : (
            title
          )}
          <Box
            id={contentContainerId}
            className={`popup-content ${bodyClassName}`}
            sx={{
              flex: 1,
              overflow: 'auto',
              padding: title ? '16px 14px' : '33px 28px',
              maxHeight: childrenMaxHeight,
              ...(hasFooter && { paddingBottom: 0 }),
            }}
          >
            {children}
          </Box>

          {/* 底部操作区 */}
          {(hasFooter || footer) && (
            <Box
              className="popup-footer"
              sx={{
                padding: '14px 16px',
                // borderTop: '1px solid #f4f4f4',
                boxShadow: '0px -2px 10px 0px rgba(204,204,204,0.5)',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              {/* 自定义 footer */}
              {footer}

              {/* 双按钮模式：确认 + 取消 */}
              {!footer && onCancel && onOk && (
                <>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      onCancel?.();
                    }}
                    sx={{
                      fontFamily: 'var(--font-bricolage-bold)',
                      height: '43px',
                    }}
                    loading={loading}
                  >
                    {defaultCancelText}
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={onOk}
                    sx={{
                      fontFamily: 'var(--font-bricolage-bold)',
                      height: '43px',
                      color: '#fff !important',
                    }}
                    loading={loading}
                    disabled={okDisabled}
                  >
                    {defaultOkText}
                  </Button>
                </>
              )}

              {/* 单按钮模式：只有确认 */}
              {!footer && onOk && !onCancel && (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={onOk}
                  sx={{
                    fontFamily: 'var(--font-bricolage-bold)',
                    height: '43px',
                    color: '#fff !important',
                  }}
                  loading={loading}
                  disabled={okDisabled}
                >
                  {defaultOkText}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Drawer>
    );
  },
);

Popup.displayName = 'Popup';

export default Popup;
